import React from 'react';
import { motion } from 'framer-motion';

const destinations = [
  {
    icon: '🏰',
    label: 'Walt Disney World y Universal',
    badge: 'Florida',
    badgeColor: 'from-pink-400 to-rose-500',
    bgColor: 'from-pink-50 to-rose-50',
    borderColor: 'border-pink-200',
  },
  {
    icon: '🎡',
    label: 'Disneyland California',
    badge: 'California',
    badgeColor: 'from-yellow-400 to-orange-500',
    bgColor: 'from-yellow-50 to-orange-50',
    borderColor: 'border-yellow-200',
  },
  {
    icon: '🎬',
    label: 'Universal Studios Hollywood',
    badge: 'Hollywood',
    badgeColor: 'from-violet-400 to-purple-500',
    bgColor: 'from-violet-50 to-purple-50',
    borderColor: 'border-violet-200',
  },
  {
    icon: '🎢',
    label: 'Universal Studios Orlando',
    badge: 'Orlando',
    badgeColor: 'from-blue-400 to-cyan-500',
    bgColor: 'from-blue-50 to-cyan-50',
    borderColor: 'border-blue-200',
  },
  {
    icon: '🚢',
    label: 'Cruceros Disney',
    badge: 'Mar Abierto',
    badgeColor: 'from-teal-400 to-emerald-500',
    bgColor: 'from-teal-50 to-emerald-50',
    borderColor: 'border-teal-200',
  },
  {
    icon: '🗼',
    label: 'Disneyland París',
    badge: 'Europa',
    badgeColor: 'from-indigo-400 to-blue-500',
    bgColor: 'from-indigo-50 to-blue-50',
    borderColor: 'border-indigo-200',
  },
];

const DestinationItem = ({ dest, index }) => (
  <motion.li
    initial={{ opacity: 0, x: -24 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.45, delay: 0.85 + index * 0.1, ease: 'easeOut' }}
    whileHover={{ scale: 1.02, x: 4 }}
    className={`flex items-center justify-between gap-3 rounded-xl px-4 py-3 border bg-gradient-to-r ${dest.bgColor} ${dest.borderColor} cursor-default transition-shadow hover:shadow-md`}
  >
    {/* Icono + texto */}
    <div className="flex items-center gap-3">
      <motion.span
        className="text-2xl"
        whileHover={{ rotate: [0, -10, 10, -6, 0] }}
        transition={{ duration: 0.5 }}
      >
        {dest.icon}
      </motion.span>
      <span className="text-gray-800 font-medium text-sm sm:text-base">
        {dest.label}
      </span>
    </div>

    {/* Badge */}
    <span
      className={`shrink-0 text-white text-xs font-semibold px-3 py-1 rounded-full bg-gradient-to-r ${dest.badgeColor} shadow-sm`}
    >
      {dest.badge}
    </span>
  </motion.li>
);

const DestinationsCard = () => (
  <motion.div
    className="bg-white/75 backdrop-blur-md rounded-2xl p-5 sm:p-6 mb-6 shadow-lg mx-2 sm:mx-0 border border-white/60"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, delay: 0.7 }}
  >
    {/* Encabezado */}
    <div className="flex items-center justify-center gap-2 mb-4">
      <span className="text-lg">✨</span>
      <p className="text-sm font-bold text-purple-700 tracking-widest uppercase">
        Ofrecemos asesoría para
      </p>
      <span className="text-lg">✨</span>
    </div>

    {/* Lista */}
    <ul className="space-y-2">
      {destinations.map((dest, i) => (
        <DestinationItem key={i} dest={dest} index={i} />
      ))}
    </ul>
  </motion.div>
);

const Hero = () => (
  <section
    id="home"
    className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-100 to-blue-100 pt-20 flex items-center justify-center relative overflow-hidden px-4 sm:px-6"
  >
    <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1578631617686-02a6758d9e89?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')] bg-cover bg-center opacity-20" />

    <motion.div
      className="max-w-3xl mx-auto text-center relative z-10 w-full"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
    >
      {/* Título */}
      <motion.div
        className="text-4xl sm:text-6xl md:text-7xl font-bold bg-gradient-to-r from-pink-500 via-purple-600 to-blue-500 bg-clip-text text-transparent mb-4"
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        🐭✨ ¡Bienvenido(a)!
      </motion.div>

      {/* Subtítulo */}
      <motion.h1
        className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      >
        al mundo mágico de Ratones Viajeros
      </motion.h1>

      {/* Descripción */}
      <motion.p
        className="text-base sm:text-lg text-gray-600 mb-6 max-w-xl mx-auto px-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
      >
        Antes de iniciar tu cotización, necesitamos saber a qué destino te gustaría viajar
        y si cuentas con los documentos necesarios para hacerlo.
      </motion.p>

      {/* Destinos */}
      <DestinationsCard />

      {/* CTA */}
      <motion.a
        href="#quote"
        className="inline-block bg-gradient-to-r from-pink-500 to-purple-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 w-full sm:w-auto max-w-xs sm:max-w-none"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.5 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        🪄 Iniciar cotización
      </motion.a>
    </motion.div>
  </section>
);

export default Hero;